import { useCallback, useEffect, useMemo, useState } from "react";
import { UserPlus, X } from "lucide-react";
import UserPickerDialog from "./UserPickerDialog";
import { useUsers } from "../hooks/useUsers";
// AuthContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useAuth } from "../../../contexts/AuthContext";
import type { MCPSharingUserItem } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";

type Props = {
  value: MCPSharingUserItem[];
  onChange: (users: MCPSharingUserItem[]) => void;
  requireCurrentUser?: boolean;
};

const UserMultiSelect = ({
  value,
  onChange,
  requireCurrentUser = false,
}: Props) => {
  const { t } = useTranslation();
  const { data: users = [], isLoading } = useUsers();
  const { userProfile, currentUser } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Names resolve against the full member list — a connection shared with the
  // signed-in user before this filter existed should still show their name.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) map.set(user.id, user.username);
    return map;
  }, [users]);

  // Sharing with yourself is meaningless: you already own what you create, and
  // some orgs return the signed-in user among their members while others do
  // not, so the option showed up inconsistently.
  //
  // Match on email first: the auth profile carries the Keycloak subject id,
  // which is a different uuid from the org member's `user.id`, so an id
  // comparison alone never matched. The id check stays as a fallback for
  // tokens issued without an email claim.
  const selfId: string | undefined = userProfile?.uid ?? currentUser?.id;
  const selfEmail: string | undefined = (
    userProfile?.email ?? currentUser?.email
  )?.toLowerCase();

  // The auth subject id and organization user id are not guaranteed to be the
  // same. Resolve the owner against the organization member list so the MCP
  // sharing payload uses the id expected by the API.
  const currentOrgUser = useMemo(() => {
    const byEmail = selfEmail
      ? users.find((user) => user.email?.toLowerCase() === selfEmail)
      : undefined;
    return byEmail ?? users.find((user) => selfId && user.id === selfId);
  }, [users, selfEmail, selfId]);

  const includeRequiredOwner = useCallback(
    (next: MCPSharingUserItem[]) => {
      if (!requireCurrentUser || !currentOrgUser) return next;
      return [
        { id: currentOrgUser.id, role: "owner" },
        ...next.filter((user) => user.id !== currentOrgUser.id),
      ];
    },
    [currentOrgUser, requireCurrentUser],
  );

  // Show the owner immediately in both create and edit modes and keep the
  // owner assignment protected before the form is submitted.
  useEffect(() => {
    if (!requireCurrentUser || !currentOrgUser) return;
    const ownerEntries = value.filter((user) => user.id === currentOrgUser.id);
    if (ownerEntries.length === 1 && ownerEntries[0].role === "owner") return;
    onChange(includeRequiredOwner(value));
  }, [
    currentOrgUser,
    includeRequiredOwner,
    onChange,
    requireCurrentUser,
    value,
  ]);

  const selectableUsers = useMemo(
    () =>
      users.filter((user) => {
        if (selfEmail && user.email?.toLowerCase() === selfEmail) return false;
        return !(selfId && user.id === selfId);
      }),
    [users, selfId, selfEmail],
  );

  const summary = isLoading
    ? t("mcpPage.loadingUsers")
    : value.length === 0
      ? t("mcpPage.selectUsers")
      : t("mcpPage.usersDialog.selectedCount", { count: value.length });

  return (
    <>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-[8px] border border-border bg-card px-3 text-sm hover:border-border/80"
      >
        <span
          className={
            value.length === 0 ? "text-muted-foreground" : "text-foreground"
          }
        >
          {summary}
        </span>
        <UserPlus className="size-4 shrink-0 text-muted-foreground" />
      </button>

      {/* Selections wrap instead of scrolling sideways, and each chip carries
          its role so the assignment is visible without reopening the picker. */}
      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {value.map((user) => {
            const name = nameById.get(user.id) ?? user.id;
            const isRequiredOwner =
              requireCurrentUser && user.id === currentOrgUser?.id;
            return (
              <li
                key={user.id}
                className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
              >
                <span>{name}</span>
                <span className="text-muted-foreground">
                  {t(`mcpPage.userRoles.${user.role}`)}
                </span>
                {!isRequiredOwner && (
                  <button
                    type="button"
                    onClick={() =>
                      onChange(value.filter((item) => item.id !== user.id))
                    }
                    aria-label={t("mcpPage.actions.remove", { name })}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <UserPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        users={selectableUsers}
        isLoading={isLoading}
        value={value}
        onConfirm={(next) => onChange(includeRequiredOwner(next))}
      />
    </>
  );
};

export default UserMultiSelect;
